import { ReactNode, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Check, Copy, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  useAgentTeamAccess,
  useGrantMemberAccess,
  useRemoveMemberAccess,
  useShareWithTeam,
} from "@/hooks/agent/useAgentAccess";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import { TeamData, TeamMember } from "@/types/auth/auth";
import ShareSettingsSection from "./ShareSettingsSection";

const DEFAULT_BRAND_COLOR = "#4E9CFF";

interface MemberRowProps {
  member: { full_name: string | null; email: string | null; avatar_url?: string | null };
  children: ReactNode;
}

function MemberRow({ member, children }: MemberRowProps) {
  return (
    <li className="flex items-center gap-3 py-1">
      <Avatar className="w-7 h-7 flex-shrink-0">
        <AvatarImage src={member.avatar_url || ""} />
        <AvatarFallback className="text-xs">{(member.full_name || member.email || "U")[0].toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex flex-1 min-w-0 items-center gap-2 text-sm">
        {member.full_name && <span className="font-medium text-gray-900 truncate shrink-0 max-w-[45%]">{member.full_name}</span>}
        <CopyableEmail
          email={member.email}
          className={member.full_name ? "text-gray-500" : "font-medium text-gray-900"}
          textClassName="truncate"
        />
      </div>
      <div className="flex-shrink-0">{children}</div>
    </li>
  );
}

interface TeamAccessSectionProps {
  genie: Agent;
  team: TeamData;
  shareUrl: string;
  userId: string;
}

export default function TeamAccessSection({ genie, team, shareUrl, userId }: TeamAccessSectionProps) {
  const [copied, setCopied] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const brandColor = genie.branding?.primaryColor || genie.brand_color;
  const { data: teamAccess = [], isLoading } = useAgentTeamAccess(genie.id);
  const shareWithTeamMutation = useShareWithTeam(genie.id);
  const grantMemberMutation = useGrantMemberAccess(genie.id);
  const removeMemberMutation = useRemoveMemberAccess(genie.id);
  const updateAgentMutation = useUpdateAgent();

  const isTeamOwner = userId === team.owner_id;
  const isPersonalGenie = userId === genie.user_id;
  const isSharedWithMe = genie.user_id !== userId;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const accessedUserIds = new Set(teamAccess.map((access) => access.user_id));
  const grantedMembers = teamAccess.map((access) => access.profile).filter(Boolean);
  const unGrantedMembers = (team.members || []).filter(
    (member) => !accessedUserIds.has(member.id) && member.id !== userId,
  );

  const handleShareWithTeam = async () => {
    shareWithTeamMutation.mutate({
      teamId: team.id,
      expiresAt: undefined,
    });
  };

  const handleGrantAccess = async (userId: string) => {
    grantMemberMutation.mutate({
      userId,
      expiresAt: undefined,
    });
  };

  const handleRemoveAccess = async (userId: string) => {
    removeMemberMutation.mutate(userId);
  };

  const handleConvertToTeamGenie = async () => {
    if (!team.owner_id) {
      toast.error("Team owner not found");
      return;
    }

    try {
      await updateAgentMutation.mutateAsync({
        agent: genie,
        updateData: { user_id: team.owner_id },
      });
      toast.success("Genie converted to team genie");
      setShowConvertConfirm(false);
    } catch (error) {
      toast.error("Failed to convert genie to team genie");
      console.error(error);
    }
  };

  const teamSummary = isTeamOwner
    ? "Shared with your whole team"
    : isSharedWithMe
      ? "Shared with you by a teammate"
      : grantedMembers.length > 0
        ? `${grantedMembers.length} of ${grantedMembers.length + unGrantedMembers.length} members have access`
        : "Only you have access";

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-gray-900 mb-2">Basic Genie Link</h4>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs bg-gray-100 px-2 py-1 rounded border border-gray-200 flex-1 truncate text-gray-700">
            {shareUrl}
          </span>
          <button
            onClick={handleCopyLink}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
            title="Copy link"
          >
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
          </button>
        </div>
      </div>

      <ShareSettingsSection title="Team Access" summary={teamSummary}>
      <div>
        {isTeamOwner ? (
          <p className="text-sm text-gray-600">Your genies are automatically shared with team members.</p>
        ) : isSharedWithMe ? (
          <p className="text-sm text-gray-600">This genie has been shared with you by a team member.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              This is your personal genie. Share it with specific team members or convert it to a team genie.
            </p>
            {grantedMembers.length === 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  onClick={() => setShowConvertConfirm(true)}
                  disabled={updateAgentMutation.isPending}
                  variant="ghost"
                  size="sm"
                  className="min-h-[44px] text-gray-600 hover:text-gray-900"
                >
                  {updateAgentMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Converting...
                    </>
                  ) : (
                    "Make Team Genie"
                  )}
                </Button>
                <Button
                  onClick={handleShareWithTeam}
                  disabled={shareWithTeamMutation.isPending}
                  variant="outline"
                  size="sm"
                  className="min-h-[44px]"
                  style={{ color: brandColor || DEFAULT_BRAND_COLOR, borderColor: `${brandColor || DEFAULT_BRAND_COLOR}66` }}
                >
                  {shareWithTeamMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sharing...
                    </>
                  ) : (
                    `Share with My Team (${unGrantedMembers.length})`
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {!isSharedWithMe && isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : !isSharedWithMe ? (
        <>
          {grantedMembers.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-1">Members with access</h5>
              <ul className="divide-y divide-gray-100">
                {grantedMembers.map((member) => (
                  <MemberRow key={member.id} member={member}>
                    <button
                      onClick={() => handleRemoveAccess(member.id)}
                      disabled={removeMemberMutation.isPending}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      aria-label="Remove access"
                      title="Remove access"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </MemberRow>
                ))}
              </ul>
            </div>
          )}

          {unGrantedMembers.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-1">Grant access to members</h5>
              <ul className="divide-y divide-gray-100">
                {unGrantedMembers.map((member: TeamMember) => (
                  <MemberRow key={member.id} member={member}>
                    <button
                      onClick={() => handleGrantAccess(member.id)}
                      disabled={grantMemberMutation.isPending}
                      className="min-h-[44px] px-3 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                      style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
                    >
                      {grantMemberMutation.isPending && grantMemberMutation.variables?.userId === member.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Grant"
                      )}
                    </button>
                  </MemberRow>
                ))}
              </ul>
            </div>
          )}

          {grantedMembers.length === 0 && unGrantedMembers.length === 0 && (
            <p className="text-sm text-gray-500">No team members available.</p>
          )}
        </>
      ) : null}
      </ShareSettingsSection>

      {showConvertConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Convert to Team Genie?</h3>
            <p className="text-gray-600 mb-6">
              This will transfer ownership of the genie to your team. All team members will automatically have access to
              this genie, and you won't be able to manage individual member access.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConvertConfirm(false)}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToTeamGenie}
                disabled={updateAgentMutation.isPending}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {updateAgentMutation.isPending ? "Converting..." : "Convert"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
